// Runtime scenario generators · v1.0
//
// CLAUDE.md §1 — every detail string is built from real graph attrs +
// env thresholds at the moment "Play scenario" is pressed. No
// hardcoded "Pendant L-887 · 92 days" text — the chandelier name, the
// lead days, the substitute, the BudgetLine values all come from
// getActiveProjectGraph() (the in-browser proxy for Oxigraph).
//
// Same renderer the agent uses for chip answers (`promptRenderer.ts`)
// but emitted as the ticker ScenarioStep shape the strip animates.

import { getActiveProjectGraph } from "../data/sampleGraph";
import type { GraphNode, GraphEdge } from "../types/ontology";
import { env } from "./env";
import type { ScenarioStep } from "./scenario";

// ── graph helpers (same swap-point as promptRenderer) ──────────────
function nodesByKind(kind: GraphNode["kind"]): GraphNode[] {
  return getActiveProjectGraph().nodes.filter((n) => n.kind === kind);
}
function neighborsBy(nodeId: string, edgeKind: GraphEdge["kind"]): GraphNode[] {
  const out: GraphNode[] = [];
  const map = new Map(getActiveProjectGraph().nodes.map((n) => [n.id, n]));
  for (const e of getActiveProjectGraph().edges) {
    if (e.kind !== edgeKind) continue;
    const other =
      e.source === nodeId ? map.get(e.target) :
      e.target === nodeId ? map.get(e.source) :
      undefined;
    if (other) out.push(other);
  }
  return out;
}
function num(node: GraphNode | undefined, key: string): number | null {
  if (!node) return null;
  const v = node.attrs?.[key];
  return typeof v === "number" ? v : null;
}
function str(node: GraphNode | undefined, key: string): string | null {
  if (!node) return null;
  const v = node.attrs?.[key];
  return typeof v === "string" ? v : null;
}
function fmtINR(n: number): string {
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
}
function roomCohesion(roomId: string): number | null {
  const skus = neighborsBy(roomId, "IN_ROOM").filter((n) => n.kind === "SKU");
  if (skus.length === 0) return null;
  const scores: number[] = [];
  for (const sku of skus) {
    for (const e of getActiveProjectGraph().edges) {
      if (e.kind === "MATCHES_STYLE" && e.source === sku.id) scores.push(e.weight);
    }
  }
  return scores.length === 0 ? null : scores.reduce((a, b) => a + b, 0) / scores.length;
}

function fallback(): ScenarioStep[] {
  // Honest, actionable: tell the user *why* we have nothing to
  // play — the project graph is missing typed Milestone / Approval
  // / Budget nodes. Foyr's export gives us rooms + items but not
  // these — they come from the designer's intake answers or the
  // partnership feed once we have it.
  const g = getActiveProjectGraph();
  const skus = g.nodes.filter((n) => n.kind === "SKU").length;
  const rooms = g.nodes.filter((n) => n.kind === "Room").length;
  const total = g.nodes.length;
  const ingested = total > 1 && skus + rooms > 0;
  return [{
    id: "fb-1",
    kind: "explain",
    label: ingested ? "Real scene loaded · scenarios need timeline data" : "Graph silent",
    detail: ingested
      ? `${skus} SKUs and ${rooms} rooms ingested from your Foyr bundle. Scenarios (lead-time, budget, approvals) need typed Milestone / Approval / BudgetLine nodes — add a deadline + budget on the project to unlock them.`
      : "Not enough data in the project graph to play this scenario yet.",
    highlightNodes: [],
    durationMs: 1500,
    badge: "info",
  }];
}

// ── Scenario 1 — Lead-time risk → substitute ────────────────────────
export function genLeadTime(): ScenarioStep[] {
  // Walk every (open-milestone × SKU) pair and score by lead-time
  // excess: (sku.leadDays) − (milestone.dueIn − buffer). The worst
  // positive excess wins. This catches the chandelier-vs-lighting
  // pairing that a milestone-first pick would miss.
  const open = nodesByKind("Milestone").filter((m) => str(m, "status") !== "done");
  if (open.length === 0) return fallback();
  const buffer = env.VITE_LEAD_TIME_BUFFER_DAYS;

  type Risk = { milestone: GraphNode; sku: GraphNode; excess: number };
  const risks: Risk[] = [];

  // Explicit BLOCKED_BY wiring wins when present — most authoritative.
  for (const m of open) {
    const dueIn = num(m, "dueIn");
    if (dueIn == null) continue;
    const blockedBy = neighborsBy(m.id, "BLOCKED_BY").filter((n) => n.kind === "SKU");
    for (const sku of blockedBy) {
      const lead = num(sku, "leadDays") ?? 0;
      risks.push({ milestone: m, sku, excess: lead - (dueIn - buffer) });
    }
  }
  // Otherwise infer: for every milestone, find every SKU whose leadDays
  // exceed the milestone's slack-adjusted runway.
  if (risks.length === 0) {
    const skus = nodesByKind("SKU");
    for (const m of open) {
      const dueIn = num(m, "dueIn");
      if (dueIn == null) continue;
      const safe = dueIn - buffer;
      for (const sku of skus) {
        const lead = num(sku, "leadDays") ?? 0;
        if (lead > safe) risks.push({ milestone: m, sku, excess: lead - safe });
      }
    }
  }
  if (risks.length === 0) return fallback();

  // Worst pair wins. Prefer pairs whose milestone is flagged red.
  risks.sort((a, b) => {
    const aRed = str(a.milestone, "status") === "red" ? 1 : 0;
    const bRed = str(b.milestone, "status") === "red" ? 1 : 0;
    if (aRed !== bRed) return bRed - aRed;
    return b.excess - a.excess;
  });
  const top = risks[0]!;
  const milestone = top.milestone;
  const offender = top.sku;
  const dueIn = num(milestone, "dueIn") ?? 0;
  const lead = num(offender, "leadDays") ?? 0;
  const price = num(offender, "price") ?? 0;
  const rule = nodesByKind("Rule")[0];
  const alert = nodesByKind("Alert").find((a) => str(a, "severity") === "warn");
  const mem = nodesByKind("MemoryCard")[0];
  // Substitute selection (in order of authority):
  // 1. Explicit SUBSTITUTES edge — most authoritative
  // 2. Same SKU category (chandelier→chandelier, sofa→sofa, …)
  // 3. Any SKU with shorter lead time as a last resort
  const substitutesByEdge = neighborsBy(offender.id, "SUBSTITUTES").filter((n) => n.kind === "SKU");
  const offenderKey = offender.label.toLowerCase().split(/[·.]/)[0]?.trim() ?? "";
  const sameCategory = nodesByKind("SKU")
    .filter((s) => s.id !== offender.id && s.label.toLowerCase().startsWith(offenderKey.slice(0, Math.max(5, offenderKey.length - 1))));
  const anyShorter = [...nodesByKind("SKU")]
    .filter((s) => s.id !== offender.id && num(s, "leadDays") != null && num(s, "leadDays")! < lead)
    .sort((a, b) => num(a, "leadDays")! - num(b, "leadDays")!);
  const alt = substitutesByEdge[0] ?? sameCategory[0] ?? anyShorter[0];

  const steps: ScenarioStep[] = [
    {
      id: "lt-1", kind: "ingest",
      label: "Item with long lead added",
      detail: `${offender.label} · ${lead}-day lead · ${fmtINR(price)}.`,
      highlightNodes: [offender.id],
      highlightTab: "catalog",
      focusNodeId: offender.id,
      durationMs: 1100,
    },
    {
      id: "lt-2", kind: "ruleFire",
      label: rule ? `${rule.label} fires` : "Lead-time rule fires",
      detail: `Lead ${lead}d > install in ${dueIn}d − ${env.VITE_LEAD_TIME_BUFFER_DAYS}d buffer.`,
      highlightNodes: [offender.id, rule?.id].filter(Boolean) as string[],
      highlightTab: "rules",
      ...((rule?.id) ? { focusNodeId: (rule?.id) as string } : {}),
      durationMs: 1100,
      badge: "warn",
    },
  ];
  if (alert) {
    steps.push({
      id: "lt-3", kind: "alert",
      label: `${milestone.label} at risk`,
      detail: `${alert.label}.`,
      highlightNodes: [offender.id, milestone.id, alert.id],
      highlightTab: "graph",
      focusNodeId: alert.id,
      durationMs: 1100,
      badge: "warn",
    });
  }
  if (mem) {
    const hits = num(mem, "hits") ?? 0, rejects = num(mem, "rejects") ?? 0;
    steps.push({
      id: "lt-4", kind: "memoryMatch",
      label: `${mem.label} matches`,
      detail: `${hits}/${rejects} accept-pattern from prior projects.`,
      highlightNodes: [offender.id, mem.id],
      highlightTab: "memory",
      focusNodeId: mem.id,
      durationMs: 1100,
      badge: "ok",
    });
  }
  if (alt) {
    const altLead = num(alt, "leadDays") ?? 0;
    const altPrice = num(alt, "price") ?? 0;
    const styleEdge = getActiveProjectGraph().edges.find((e) => e.kind === "MATCHES_STYLE" && e.source === alt.id);
    const stylePct = styleEdge ? Math.round(styleEdge.weight * 100) : 0;
    steps.push({
      id: "lt-5", kind: "substitute",
      label: "Substitute proposed",
      detail: `${alt.label} · ${altLead}-day lead · ${stylePct}% style match · ΔPrice ${fmtINR(altPrice - price)}.`,
      highlightNodes: [offender.id, alt.id, mem?.id].filter(Boolean) as string[],
      highlightTab: "catalog",
      focusNodeId: alt.id,
      durationMs: 1400,
      badge: "money",
      delta: `−${Math.max(0, lead - altLead)} days · ${stylePct}%`,
    });
  }
  return steps;
}

// ── Scenario 2 — Budget cap drops 12% → live redistribution ────────
export function genBudget(cutPercent = 12): ScenarioStep[] {
  const project = nodesByKind("Project")[0];
  const cap = num(project, "budgetCap");
  if (!project || cap == null) return fallback();
  const newCap = Math.round(cap * (1 - cutPercent / 100));
  const lines = nodesByKind("BudgetLine").filter((l) => num(l, "amount") != null);
  if (lines.length === 0) return fallback();
  const target = env.VITE_BUDGET_LINE_TARGET;
  const tolerance = env.VITE_COHESION_TOLERANCE_PP;
  const sortedHigh = [...lines].sort((a, b) => num(b, "amount")! - num(a, "amount")!);
  const breaches = sortedHigh.filter((l) => num(l, "amount")! / newCap > target);
  const donor = [...lines].sort((a, b) => num(a, "amount")! - num(b, "amount")!)[0];
  const recipient = breaches[0] ?? sortedHigh[0];
  const rule = nodesByKind("Rule").find((r) => /budget/i.test(r.label));

  const steps: ScenarioStep[] = [
    {
      id: "bd-1", kind: "ingest",
      label: "Budget cap reset",
      detail: `${fmtINR(cap)} → ${fmtINR(newCap)} (−${cutPercent}%).`,
      highlightNodes: [project.id],
      highlightTab: "graph",
      focusNodeId: project.id,
      durationMs: 1000,
    },
    {
      id: "bd-2", kind: "budgetSlip",
      label: `${breaches.length} room${breaches.length === 1 ? "" : "s"} breach`,
      detail: breaches.slice(0, 2).map((b) => `${b.label} (${fmtINR(num(b, "amount")!)})`).join(" · "),
      highlightNodes: breaches.slice(0, 2).map((b) => b.id),
      highlightTab: "graph",
      ...((breaches[0]?.id) ? { focusNodeId: (breaches[0]?.id) as string } : {}),
      durationMs: 1200,
      badge: "warn",
    },
  ];
  if (rule && recipient) {
    steps.push({
      id: "bd-3", kind: "ruleFire",
      label: `${rule.label} fires`,
      detail: `BudgetLine share > ${Math.round(target * 100)}% on a room — flagged.`,
      highlightNodes: [recipient.id, rule.id],
      highlightTab: "rules",
      focusNodeId: rule.id,
      durationMs: 1000,
      badge: "warn",
    });
  }
  if (donor && recipient) {
    steps.push({
      id: "bd-4", kind: "substitute",
      label: "Redistribution suggested",
      detail: `Shift from ${donor.label} → ${recipient.label}; stays within ${tolerance} pp cohesion tolerance.`,
      highlightNodes: [donor.id, recipient.id],
      highlightTab: "graph",
      durationMs: 1400,
      badge: "money",
      delta: `fits new cap`,
    });
  }
  return steps;
}

// ── Scenario 3 — Style drift before render ─────────────────────────
export function genStyleDrift(): ScenarioStep[] {
  const intent = nodesByKind("Intent")[0];
  const threshold = env.VITE_STYLE_COHESION_THRESHOLD;
  const rooms = nodesByKind("Room");
  const scored = rooms
    .map((r) => ({ room: r, cohesion: roomCohesion(r.id) }))
    .filter((s): s is { room: GraphNode; cohesion: number } => s.cohesion != null)
    .sort((a, b) => a.cohesion - b.cohesion);
  if (scored.length === 0 || !intent) return fallback();
  const worst = scored[0]!;
  if (worst.cohesion >= threshold) {
    return [{
      id: "sd-ok", kind: "explain",
      label: "All rooms within style threshold",
      detail: `Lowest cohesion: ${worst.room.label} @ ${worst.cohesion.toFixed(2)} (≥ ${threshold.toFixed(2)}). Safe to render.`,
      highlightNodes: [worst.room.id],
      highlightTab: "inspiration",
      focusNodeId: worst.room.id,
      durationMs: 1400,
      badge: "ok",
    }];
  }
  const inRoom = neighborsBy(worst.room.id, "IN_ROOM").filter((n) => n.kind === "SKU");
  const outlier = [...inRoom]
    .filter((s) => num(s, "styleMatch") != null)
    .sort((a, b) => num(a, "styleMatch")! - num(b, "styleMatch")!)[0];

  return [
    {
      id: "sd-1", kind: "ingest",
      label: "Pre-render check",
      detail: `${worst.room.label} cohesion ${worst.cohesion.toFixed(2)}.`,
      highlightNodes: [worst.room.id],
      highlightTab: "inspiration",
      focusNodeId: worst.room.id,
      durationMs: 1000,
    },
    {
      id: "sd-2", kind: "styleDrift",
      label: "Below the look threshold",
      detail: `${worst.cohesion.toFixed(2)} < Intent threshold ${threshold.toFixed(2)}.`,
      highlightNodes: [worst.room.id, intent.id],
      highlightTab: "inspiration",
      focusNodeId: intent.id,
      durationMs: 1100,
      badge: "warn",
    },
    ...(outlier ? [{
      id: "sd-3", kind: "alert" as const,
      label: "Outlier piece",
      detail: `${outlier.label} · style match ${(num(outlier, "styleMatch") ?? 0).toFixed(2)} — restyle before render.`,
      highlightNodes: [worst.room.id, outlier.id],
      highlightTab: "catalog" as const,
      focusNodeId: outlier.id,
      durationMs: 1200,
      badge: "money" as const,
      delta: "render avoided",
    }] : []),
  ];
}

// ── Scenario 4 — Approval gone quiet ────────────────────────────────
export function genApproval(): ScenarioStep[] {
  const project = nodesByKind("Project")[0];
  const approvals = nodesByKind("Approval");
  if (!project || approvals.length === 0) return fallback();
  const threshold = env.VITE_APPROVAL_AGE_DAYS;
  const aged = approvals
    .map((a) => ({ node: a, age: num(a, "ageDays") }))
    .filter((r): r is { node: GraphNode; age: number } => r.age != null)
    .sort((a, b) => b.age - a.age);
  if (aged.length === 0) return fallback();
  const oldest = aged[0]!;
  const handover = nodesByKind("Milestone").find((m) => /handover/i.test(m.label));
  return [
    {
      id: "ap-1", kind: "ingest",
      label: "Approval aged",
      detail: `${oldest.node.label} · ${oldest.age} days old.`,
      highlightNodes: [oldest.node.id],
      highlightTab: "graph",
      focusNodeId: oldest.node.id,
      durationMs: 1000,
    },
    {
      id: "ap-2", kind: "approvalAged",
      label: "Past freshness threshold",
      detail: `Studio threshold ${threshold} d · this one is ${oldest.age} d — handover at risk.`,
      highlightNodes: [oldest.node.id, handover?.id].filter(Boolean) as string[],
      highlightTab: "graph",
      focusNodeId: handover?.id ?? oldest.node.id,
      durationMs: 1100,
      badge: "warn",
    },
    {
      id: "ap-3", kind: "memoryMatch",
      label: "Refresh packet drafted",
      detail: `Pre-filled with the project's prior approval text. You review and forward.`,
      highlightNodes: [oldest.node.id],
      highlightTab: "memory",
      durationMs: 1200,
      badge: "money",
      delta: "no slip",
    },
  ];
}

// ── Scenario 5 — Rule promotion ─────────────────────────────────────
export function genRulePromotion(): ScenarioStep[] {
  const rules = nodesByKind("Rule");
  if (rules.length === 0) return fallback();
  const minAccepts = env.VITE_MEMORY_MIN_ACCEPTS;
  const maxReject = env.VITE_MEMORY_MAX_REJECT_RATIO;
  const scored = rules.map((r) => {
    const fired = num(r, "firedCount") ?? 0;
    const accepted = num(r, "acceptedCount") ?? 0;
    const rejected = num(r, "rejectedCount") ?? 0;
    const ratio = fired > 0 ? rejected / fired : 1;
    return { rule: r, fired, accepted, rejected, ratio };
  }).sort((a, b) => b.accepted - a.accepted);
  const top = scored[0]!;
  const mem = nodesByKind("MemoryCard")[0];

  if (top.fired < minAccepts || top.ratio > maxReject) {
    return [{
      id: "rp-wait", kind: "explain",
      label: "Promotion still warming up",
      detail: `${top.rule.label}: ${top.fired} fires, ${top.accepted}/${top.rejected} accept/reject. Needs ≥ ${minAccepts} fires with reject ratio ≤ ${maxReject.toFixed(2)}.`,
      highlightNodes: [top.rule.id],
      highlightTab: "rules",
      focusNodeId: top.rule.id,
      durationMs: 1500,
      badge: "info",
    }];
  }

  return [
    {
      id: "rp-1", kind: "ruleFire",
      label: `${top.rule.label} — strong record`,
      detail: `${top.fired} fires · ${top.accepted}/${top.rejected} accept/reject.`,
      highlightNodes: [top.rule.id],
      highlightTab: "rules",
      focusNodeId: top.rule.id,
      durationMs: 1100,
      badge: "ok",
    },
    {
      id: "rp-2", kind: "memoryPromote",
      label: "Promotion threshold met",
      detail: `≥ ${minAccepts} fires AND reject ratio ≤ ${maxReject.toFixed(2)} — lifts into studio memory.`,
      highlightNodes: [top.rule.id, mem?.id].filter(Boolean) as string[],
      highlightTab: "memory",
      ...((mem?.id) ? { focusNodeId: (mem?.id) as string } : {}),
      durationMs: 1200,
      badge: "money",
      delta: "+1 studio default",
    },
    ...(mem ? [{
      id: "rp-3", kind: "memoryMatch" as const,
      label: "Available on every new project",
      detail: `${mem.label} now consulted by every future project of the studio.`,
      highlightNodes: [mem.id],
      highlightTab: "memory" as const,
      durationMs: 1100,
      badge: "ok" as const,
    }] : []),
  ];
}

// ── helper used by the post-swap generators below — finds the worst
//    lead-time risk pair without re-running the whole genLeadTime body.
function _worstLeadPair(): { offender: GraphNode; substitute: GraphNode | null; milestone: GraphNode } | null {
  const open = nodesByKind("Milestone").filter((m) => str(m, "status") !== "done");
  if (open.length === 0) return null;
  const buffer = env.VITE_LEAD_TIME_BUFFER_DAYS;
  type Risk = { milestone: GraphNode; sku: GraphNode; excess: number };
  const risks: Risk[] = [];
  const skus = nodesByKind("SKU");
  for (const m of open) {
    const dueIn = num(m, "dueIn");
    if (dueIn == null) continue;
    const safe = dueIn - buffer;
    for (const s of skus) {
      const lead = num(s, "leadDays") ?? 0;
      if (lead > safe) risks.push({ milestone: m, sku: s, excess: lead - safe });
    }
  }
  if (risks.length === 0) return null;
  risks.sort((a, b) => b.excess - a.excess);
  const top = risks[0]!;
  // Pick a real substitute via SUBSTITUTES edge, then category, then any shorter.
  const lead = num(top.sku, "leadDays") ?? 0;
  const offKey = top.sku.label.toLowerCase().split(/[·.]/)[0]?.trim() ?? "";
  const bySubs = neighborsBy(top.sku.id, "SUBSTITUTES").filter((n) => n.kind === "SKU");
  const byCat = nodesByKind("SKU").filter((s) => s.id !== top.sku.id && s.label.toLowerCase().startsWith(offKey.slice(0, Math.max(5, offKey.length - 1))));
  const byLead = [...nodesByKind("SKU")]
    .filter((s) => s.id !== top.sku.id && (num(s, "leadDays") ?? Infinity) < lead)
    .sort((a, b) => (num(a, "leadDays")! - num(b, "leadDays")!));
  const sub = bySubs[0] ?? byCat[0] ?? byLead[0] ?? null;
  return { offender: top.sku, substitute: sub, milestone: top.milestone };
}

// ── Scenario 6 — Email Drafter (Self-Refine) ────────────────────────
export function genEmailDraft(): ScenarioStep[] {
  const pair = _worstLeadPair();
  if (!pair || !pair.substitute) return fallback();
  const { offender, substitute, milestone } = pair;
  const vendor = neighborsBy(substitute.id, "OFFERS").find((n) => n.kind === "Vendor")
                ?? neighborsBy(substitute.id, "SUPPLIED_BY").find((n) => n.kind === "Vendor")
                ?? null;
  const client = nodesByKind("Client")[0] ?? null;
  const project = nodesByKind("Project")[0] ?? null;
  const cited = [offender.id, substitute.id, vendor?.id, milestone.id, client?.id, project?.id].filter(Boolean) as string[];

  return [
    {
      id: "ed-1", kind: "ingest",
      label: "Substitute confirmed",
      detail: `${substitute.label} replaces ${offender.label} — Email Drafter agent picks up the change.`,
      highlightNodes: [offender.id, substitute.id],
      highlightTab: "graph",
      focusNodeId: substitute.id,
      durationMs: 1000,
    },
    {
      id: "ed-2", kind: "ruleFire",
      label: "Self-Refine draft pass",
      detail: `Nemotron via APISIX · target: Gmail draft for ${client?.label ?? "the client"}.`,
      highlightNodes: [substitute.id, vendor?.id].filter(Boolean) as string[],
      highlightTab: "rules",
      durationMs: 1100,
      badge: "ok",
    },
    {
      id: "ed-3", kind: "memoryMatch",
      label: "Critic enforces contract",
      detail: "Four checks: no invented facts · one clear ask · ≤ 140 words · tone match.",
      highlightNodes: [substitute.id],
      highlightTab: "memory",
      durationMs: 1100,
      badge: "ok",
    },
    {
      id: "ed-4", kind: "substitute",
      label: "Draft ready — awaiting your nod",
      detail: `Cites ${cited.length} graph nodes · 0 auto-sends · requires_human_review = true.`,
      highlightNodes: cited,
      highlightTab: "memory",
      durationMs: 1400,
      badge: "money",
      delta: `cites ${cited.length} nodes`,
    },
  ];
}

// ── Scenario 7 — Project & Tasks AI (CP-SAT reschedule) ─────────────
export function genSprintReschedule(): ScenarioStep[] {
  const pair = _worstLeadPair();
  if (!pair || !pair.substitute) return fallback();
  const { offender, substitute, milestone } = pair;
  const open = nodesByKind("Milestone").filter((m) => str(m, "status") !== "done");
  const offenderLead = num(offender, "leadDays") ?? 0;
  const subLead = num(substitute, "leadDays") ?? 0;
  const delta = offenderLead - subLead;
  const slackBefore = num(milestone, "slackDays") ?? 0;
  const slackAfter = slackBefore + Math.max(0, delta - env.VITE_LEAD_TIME_BUFFER_DAYS);
  const handover = open.find((m) => /handover/i.test(m.label));

  return [
    {
      id: "sr-1", kind: "ingest",
      label: "Lead-time delta absorbed",
      detail: `${substitute.label} lead ${subLead}d (was ${offenderLead}d) — schedule needs a refresh.`,
      highlightNodes: [substitute.id, milestone.id],
      highlightTab: "graph",
      focusNodeId: milestone.id,
      durationMs: 1000,
    },
    {
      id: "sr-2", kind: "ruleFire",
      label: "Project & Tasks agent solves",
      detail: "OR-Tools CP-SAT · minimise makespan, then minimise resource overload.",
      highlightNodes: open.slice(0, 3).map((m) => m.id),
      highlightTab: "rules",
      durationMs: 1100,
      badge: "ok",
    },
    {
      id: "sr-3", kind: "alert",
      label: "Critical path resolves",
      detail: `${milestone.label} slack ${slackBefore}d → ${slackAfter}d${handover ? `; ${handover.label} stays clear` : ""}.`,
      highlightNodes: [milestone.id, handover?.id].filter(Boolean) as string[],
      highlightTab: "graph",
      ...(handover?.id ? { focusNodeId: handover.id } : { focusNodeId: milestone.id }),
      durationMs: 1100,
      badge: "ok",
    },
    {
      id: "sr-4", kind: "substitute",
      label: "Plan published",
      detail: `${open.length} open milestones · CP-SAT feasible · assignments held by skill_tag match.`,
      highlightNodes: open.map((m) => m.id),
      highlightTab: "graph",
      durationMs: 1300,
      badge: "money",
      delta: `+${Math.max(0, slackAfter - slackBefore)} days slack`,
    },
  ];
}

// ── Scenario 8 — Estimate + Change Order ────────────────────────────
export function genEstimateUpdate(): ScenarioStep[] {
  const pair = _worstLeadPair();
  if (!pair || !pair.substitute) return fallback();
  const { offender, substitute } = pair;
  const p1 = num(offender, "price") ?? 0;
  const p2 = num(substitute, "price") ?? 0;
  const dPrice = p2 - p1;
  const lines = nodesByKind("BudgetLine");
  const taxRate = 18;   // GST — kept as percentage label only; real arithmetic done server-side in agents/estimate.py
  const cited = [offender.id, substitute.id, ...lines.slice(0, 2).map((l) => l.id)];

  return [
    {
      id: "eu-1", kind: "ingest",
      label: "Line delta detected",
      detail: `${substitute.label} ${fmtINR(p2)} replaces ${offender.label} ${fmtINR(p1)} · ΔPrice ${dPrice >= 0 ? "+" : ""}${fmtINR(dPrice)}.`,
      highlightNodes: [offender.id, substitute.id, lines[0]?.id].filter(Boolean) as string[],
      highlightTab: "graph",
      ...(lines[0]?.id ? { focusNodeId: lines[0].id } : {}),
      durationMs: 1000,
    },
    {
      id: "eu-2", kind: "ruleFire",
      label: "Estimate agent recomputes",
      detail: `Pydantic Decimal math · ${taxRate}% GST · cover letter via narrator agent.`,
      highlightNodes: lines.slice(0, 3).map((l) => l.id),
      highlightTab: "rules",
      durationMs: 1100,
      badge: "ok",
    },
    {
      id: "eu-3", kind: "memoryMatch",
      label: "Change Order saga starts",
      detail: "n8n workflow fm-change-order-saga: draft → wait → branch → amend or compensate.",
      highlightNodes: lines.slice(0, 2).map((l) => l.id),
      highlightTab: "memory",
      durationMs: 1100,
      badge: "ok",
    },
    {
      id: "eu-4", kind: "substitute",
      label: "Drafts ready",
      detail: `Estimate-v2 + ChangeOrder-001 · requires_human_review = true · 0 auto-sends.`,
      highlightNodes: cited,
      highlightTab: "memory",
      durationMs: 1400,
      badge: "money",
      delta: `${dPrice >= 0 ? "+" : ""}${fmtINR(dPrice)}`,
    },
  ];
}

// ── Scenario 9 — Scheduling AI (site visit) ─────────────────────────
export function genSiteVisit(): ScenarioStep[] {
  const project = nodesByKind("Project")[0];
  if (!project) return fallback();
  const open = nodesByKind("Milestone")
    .filter((m) => str(m, "status") !== "done" && num(m, "dueIn") != null)
    .sort((a, b) => (num(a, "dueIn")! - num(b, "dueIn")!));
  const next = open[0];
  if (!next) return fallback();
  const dueIn = num(next, "dueIn") ?? 0;
  const designer = "Designer";
  const client = nodesByKind("Client")[0]?.label ?? "Client";
  const civilLikely = open.find((m) => /civil|construct|hand-?off|kitchen|install/i.test(m.label));
  const buffer = env.VITE_SCHEDULE_TRAVEL_BUFFER_MIN;

  return [
    {
      id: "sv-1", kind: "ingest",
      label: `${next.label} ${dueIn} days out`,
      detail: `Project ${project.label} · 3 stakeholders need a walkthrough before this milestone.`,
      highlightNodes: [next.id, project.id],
      highlightTab: "graph",
      focusNodeId: next.id,
      durationMs: 1000,
    },
    {
      id: "sv-2", kind: "ruleFire",
      label: "Scheduling agent intersects calendars",
      detail: `${designer} · ${client} · ${civilLikely ? civilLikely.label.split(/\s+/)[0] : "Site engineer"} windows + ${buffer}-min travel buffer.`,
      highlightNodes: [next.id, civilLikely?.id].filter(Boolean) as string[],
      highlightTab: "rules",
      durationMs: 1100,
      badge: "ok",
    },
    {
      id: "sv-3", kind: "substitute",
      label: "Earliest valid slot found",
      detail: `Tentative invite · location: ${project.label} · awaiting client confirm.`,
      highlightNodes: [next.id, project.id],
      highlightTab: "graph",
      durationMs: 1300,
      badge: "money",
      delta: "tentative",
    },
  ];
}

export const SCENARIO_GENERATORS: Record<string, () => ScenarioStep[]> = {
  "lead-time-risk":    genLeadTime,
  "budget-drop":       genBudget,
  "style-drift":       genStyleDrift,
  "rera-aging":        genApproval,
  "memory-promote":    genRulePromotion,
  // ── Foyr-Manage-parity additions (new agents · live graph walks) ──
  "email-draft":       genEmailDraft,
  "sprint-reschedule": genSprintReschedule,
  "estimate-update":   genEstimateUpdate,
  "site-visit":        genSiteVisit,
};

// Live subtitle composed from the current graph state. Same generator
// shape; just produces one summary string instead of step list.
/* Live, project-aware titles. The cockpit prefers these over the static
   titles in scenario.ts so switching projects rewrites the chip label. */
export function genScenarioTitle(id: string): string | null {
  switch (id) {
    case "lead-time-risk": {
      const p = _worstLeadPair();
      if (!p) return null;
      const item = p.offender.label.split("·")[0]!.trim().toLowerCase();
      return `A ${item} won't arrive in time — find a swap`;
    }
    case "budget-drop": {
      const project = nodesByKind("Project")[0];
      const cap = num(project, "budgetCap");
      if (cap == null) return null;
      return `Client trimmed the budget — protect the rooms that matter (${fmtINR(cap)})`;
    }
    case "style-drift": {
      const rooms = nodesByKind("Room")
        .map((r) => ({ r, c: roomCohesion(r.id) }))
        .filter((x): x is { r: GraphNode; c: number } => x.c != null)
        .sort((a, b) => a.c - b.c);
      const worst = rooms[0];
      if (!worst) return null;
      return `One piece looks off in ${worst.r.label} — catch it before render`;
    }
    case "rera-aging": {
      const aged = nodesByKind("Approval")
        .map((a) => ({ a, age: num(a, "ageDays") }))
        .filter((x): x is { a: GraphNode; age: number } => x.age != null)
        .sort((a, b) => b.age - a.age);
      const oldest = aged[0];
      if (!oldest) return "An approval has gone quiet — give it a nudge";
      return `${oldest.a.label} is ${oldest.age} days quiet — nudge`;
    }
    case "memory-promote": {
      const top = nodesByKind("Rule")
        .map((r) => ({ r, fired: num(r, "firedCount") ?? 0 }))
        .sort((a, b) => b.fired - a.fired)[0];
      if (!top) return null;
      return `${top.r.label} has fired ${top.fired}× — promote to studio default`;
    }
    case "email-draft": {
      const p = _worstLeadPair();
      if (!p?.substitute) return null;
      const client = nodesByKind("Client")[0]?.label ?? "the client";
      return `Draft ${client} about the ${p.offender.label.split("·")[0]!.trim().toLowerCase()} swap`;
    }
    case "sprint-reschedule": {
      const open = nodesByKind("Milestone").filter((m) => str(m, "status") !== "done").length;
      return `Re-solve ${open} open milestones with the new lead time`;
    }
    case "estimate-update": {
      const p = _worstLeadPair();
      if (!p?.substitute) return null;
      const d = (num(p.substitute, "price") ?? 0) - (num(p.offender, "price") ?? 0);
      return `Update the estimate · ΔPrice ${d >= 0 ? "+" : ""}${fmtINR(d)}`;
    }
    case "site-visit": {
      const open = nodesByKind("Milestone")
        .filter((m) => str(m, "status") !== "done" && num(m, "dueIn") != null)
        .sort((a, b) => (num(a, "dueIn")! - num(b, "dueIn")!));
      const next = open[0];
      if (!next) return null;
      return `Book a site visit before ${next.label}`;
    }
    default: return null;
  }
}

/* Live outcome chip — derived from the FINAL step of whatever the
 * generator produced. Replaces the static `selected.outcome` literal
 * so the cockpit's outcome pill is always graph-accurate. */
export function genScenarioOutcome(id: string): string | null {
  const gen = SCENARIO_GENERATORS[id];
  if (!gen) return null;
  let steps: ScenarioStep[];
  try { steps = gen(); } catch { return null; }
  if (steps.length === 0) return null;
  const last = steps[steps.length - 1]!;
  // Prefer the structured `delta` field, fall through to a short
  // composed sentence from the last step's detail.
  if (last.delta) return last.delta;
  const detail = last.detail.replace(/\s+/g, " ").trim();
  return detail.length > 64 ? detail.slice(0, 60) + "…" : detail;
}

export function genScenarioSubtitle(id: string): string | null {
  switch (id) {
    case "lead-time-risk": {
      const open = nodesByKind("Milestone").filter((m) => str(m, "status") !== "done");
      if (open.length === 0) return null;
      const m = open.find((x) => str(x, "status") === "red")
        ?? [...open].sort((a, b) => (num(a, "slackDays") ?? Infinity) - (num(b, "slackDays") ?? Infinity))[0]!;
      const dueIn = num(m, "dueIn");
      const buf = env.VITE_LEAD_TIME_BUFFER_DAYS;
      if (dueIn == null) return null;
      const offender = [...nodesByKind("SKU")]
        .filter((s) => (num(s, "leadDays") ?? 0) > dueIn - buf)
        .sort((a, b) => (num(b, "leadDays") ?? -Infinity) - (num(a, "leadDays") ?? -Infinity))[0];
      if (!offender) return null;
      return `${offender.label} (${num(offender, "leadDays")} d) vs ${m.label} (${dueIn} d).`;
    }
    case "budget-drop": {
      const project = nodesByKind("Project")[0];
      const cap = num(project, "budgetCap");
      if (cap == null) return null;
      return `Cap ${fmtINR(cap)} · breach target ${Math.round(env.VITE_BUDGET_LINE_TARGET * 100)}% per room.`;
    }
    case "style-drift": {
      const rooms = nodesByKind("Room")
        .map((r) => ({ r, c: roomCohesion(r.id) }))
        .filter((x): x is { r: GraphNode; c: number } => x.c != null)
        .sort((a, b) => a.c - b.c);
      const worst = rooms[0];
      if (!worst) return null;
      return `${worst.r.label}: cohesion ${worst.c.toFixed(2)} vs threshold ${env.VITE_STYLE_COHESION_THRESHOLD}.`;
    }
    case "rera-aging": {
      const aged = nodesByKind("Approval")
        .map((a) => ({ a, age: num(a, "ageDays") }))
        .filter((x): x is { a: GraphNode; age: number } => x.age != null)
        .sort((a, b) => b.age - a.age);
      const oldest = aged[0];
      if (!oldest) return null;
      return `${oldest.a.label}: ${oldest.age} d (threshold ${env.VITE_APPROVAL_AGE_DAYS} d).`;
    }
    case "memory-promote": {
      const top = nodesByKind("Rule")
        .map((r) => ({
          r,
          fired: num(r, "firedCount") ?? 0,
          accepted: num(r, "acceptedCount") ?? 0,
        }))
        .sort((a, b) => b.accepted - a.accepted)[0];
      if (!top) return null;
      return `${top.r.label}: ${top.fired} fires · ${top.accepted} accepts (gate ${env.VITE_MEMORY_MIN_ACCEPTS}).`;
    }
    case "email-draft": {
      const p = _worstLeadPair();
      if (!p?.substitute) return null;
      return `Draft to client about ${p.offender.label} → ${p.substitute.label}.`;
    }
    case "sprint-reschedule": {
      const open = nodesByKind("Milestone").filter((m) => str(m, "status") !== "done").length;
      return `Re-solve ${open} open milestones via OR-Tools CP-SAT.`;
    }
    case "estimate-update": {
      const p = _worstLeadPair();
      if (!p?.substitute) return null;
      const d = (num(p.substitute, "price") ?? 0) - (num(p.offender, "price") ?? 0);
      return `ΔPrice ${d >= 0 ? "+" : ""}${fmtINR(d)} · Change Order drafts itself.`;
    }
    case "site-visit": {
      const open = nodesByKind("Milestone")
        .filter((m) => str(m, "status") !== "done" && num(m, "dueIn") != null)
        .sort((a, b) => (num(a, "dueIn")! - num(b, "dueIn")!));
      const next = open[0];
      if (!next) return null;
      return `Earliest slot before ${next.label} (${num(next, "dueIn")} d).`;
    }
    default: return null;
  }
}

// Severity score in [0, 1] for "how active is this scenario right now."
// The cockpit auto-picks the highest-scoring scenario so Maya sees the
// most urgent flow first without clicking around.
export function scenarioUrgency(id: string): number {
  switch (id) {
    case "lead-time-risk": {
      const open = nodesByKind("Milestone").filter((m) => str(m, "status") !== "done");
      const red = open.some((m) => str(m, "status") === "red");
      return red ? 0.95 : open.length > 0 ? 0.6 : 0;
    }
    case "budget-drop": {
      // Always somewhat relevant; project always has a budget.
      return 0.55;
    }
    case "style-drift": {
      const rooms = nodesByKind("Room")
        .map((r) => roomCohesion(r.id))
        .filter((c): c is number => c != null);
      if (rooms.length === 0) return 0;
      const worst = Math.min(...rooms);
      return worst < env.VITE_STYLE_COHESION_THRESHOLD ? 0.85 : 0.3;
    }
    case "rera-aging": {
      const aged = nodesByKind("Approval")
        .map((a) => num(a, "ageDays") ?? 0)
        .filter((a) => a > 0);
      const max = aged.length > 0 ? Math.max(...aged) : 0;
      return max >= env.VITE_APPROVAL_AGE_DAYS ? 0.9 : 0.2;
    }
    case "memory-promote": {
      const top = nodesByKind("Rule")
        .map((r) => num(r, "firedCount") ?? 0)
        .sort((a, b) => b - a)[0] ?? 0;
      return top >= env.VITE_MEMORY_MIN_ACCEPTS ? 0.75 : 0.35;
    }
    case "email-draft":       return _worstLeadPair()?.substitute ? 0.7 : 0.2;
    case "sprint-reschedule": return _worstLeadPair() ? 0.6 : 0.2;
    case "estimate-update":   return _worstLeadPair()?.substitute ? 0.65 : 0.2;
    case "site-visit": {
      const open = nodesByKind("Milestone")
        .filter((m) => str(m, "status") !== "done" && (num(m, "dueIn") ?? Infinity) < 60);
      return open.length > 0 ? 0.55 : 0.2;
    }
    default: return 0;
  }
}

// Naive intent router for free-text "Ask me a what-if" input. Returns
// the scenario id whose keywords match the user's text best, or null
// if nothing matches. Same shape the live agent uses (just a smaller
// version of the prompt-catalog regex router).
export function routeFreeTextToScenario(text: string): string | null {
  const t = text.toLowerCase().trim();
  if (t.length < 4) return null;
  const rules: Array<{ id: string; patterns: RegExp[] }> = [
    { id: "lead-time-risk",  patterns: [/late|lead.?time|delay|won'?t arrive|chandelier|vendor.*slow/i] },
    { id: "budget-drop",     patterns: [/budget|cut|trim|reduce|cap|over.?run|cost/i] },
    { id: "style-drift",     patterns: [/style|cohesion|render|moodboard|look|outlier|off/i] },
    { id: "rera-aging",      patterns: [/approv|rera|sign.?off|handover|stale|pending|client.*wait/i] },
    { id: "memory-promote",  patterns: [/rule|habit|memor|promote|studio.*default|reusable/i] },
  ];
  for (const r of rules) {
    if (r.patterns.some((p) => p.test(t))) return r.id;
  }
  return null;
}
